import React, { useState } from 'react';
import { Send, Calendar, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';

const CTASection = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Mock submission - will integrate with backend later
    setTimeout(() => {
      toast({
        title: "Message Sent! 🎉",
        description: "I'll get back to you within 24 hours. Let's build something amazing together!",
      });
      setFormData({ name: '', email: '', company: '', message: '' });
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <section id="contact" className="bg-gradient-to-b from-[#0a0a0a] to-black py-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-block mb-4 px-4 py-2 bg-[#ff6b35]/10 border border-[#ff6b35]/30 rounded-full">
            <span className="text-[#ff6b35] font-semibold text-sm tracking-wider uppercase">
              Let's Connect
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-black text-white mb-4">
            Let's Build a Stronger Brand Together
          </h2>
          <p className="text-xl text-gray-400 mb-6">
            Ready to create content that converts? Let's talk about your goals.
          </p>
          <div className="inline-flex items-center gap-2 bg-[#ff6b35]/20 border border-[#ff6b35] text-[#ff6b35] px-4 py-2 rounded-lg">
            <AlertCircle size={18} />
            <span className="font-bold text-sm">Limited to 3-5 new partnerships per month</span>
          </div>
        </div>

        <div className="bg-gradient-to-br from-gray-900 to-black border border-gray-800 rounded-lg p-8 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-white font-semibold mb-2">
                  Your Name *
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="bg-black border-gray-700 text-white focus:border-[#00ff41] focus:ring-[#00ff41]"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-white font-semibold mb-2">
                  Email Address *
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="bg-black border-gray-700 text-white focus:border-[#00ff41] focus:ring-[#00ff41]"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="company" className="block text-white font-semibold mb-2">
                Company/Brand Name *
              </label>
              <Input
                id="company"
                name="company"
                type="text"
                required
                value={formData.company}
                onChange={handleChange}
                className="bg-black border-gray-700 text-white focus:border-[#00ff41] focus:ring-[#00ff41]"
                placeholder="Your Fitness Brand"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-white font-semibold mb-2">
                Tell Me About Your Project *
              </label>
              <Textarea
                id="message"
                name="message"
                required
                value={formData.message}
                onChange={handleChange}
                rows={5}
                className="bg-black border-gray-700 text-white focus:border-[#00ff41] focus:ring-[#00ff41] resize-none"
                placeholder="What are your goals? What type of partnership are you interested in?"
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#00ff41] text-black hover:bg-[#00ff41]/90 font-bold text-lg py-6 group"
            >
              {isSubmitting ? (
                'Sending...'
              ) : (
                <>
                  Send Message
                  <Send className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-800 text-center">
            <p className="text-gray-400 mb-3">Or schedule a call directly</p>
            <Button
              variant="outline"
              className="border-[#ff6b35] text-[#ff6b35] hover:bg-[#ff6b35] hover:text-black"
              onClick={() => window.open('https://calendly.com', '_blank')}
            >
              <Calendar className="mr-2" size={18} />
              Book a Consultation
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
